// Serviço do Assistente Geral "H.Bot" (Frente 3, G10) — namespace `/api/assistant/`.
//
// Conversas, mensagens (GET — o POST de envio vai por `assistant.stream.ts`),
// anexos transientes (G04), URL de download de arquivo gerado (G03) e memória (G07).

import apiService from './api.service';
import {
  AssistantAttachment,
  AssistantConversationDetail,
  AssistantConversationListItem,
  AssistantMemoryResponse,
  AssistantMessage,
  AssistantSavedMemory,
  AssistantSummary,
  CompanyOption,
} from '@/types/assistant';

// Re-export do helper SSE pra importar de um único lugar (espelha agents.service).
export {
  streamAssistantMessage,
  submitElicitation,
  AssistantQuotaStreamError,
  isAssistantQuotaStreamError,
} from './assistant.stream';

type Paginated<T> = T[] | { results: T[] };

function unwrapList<T>(data: Paginated<T>): T[] {
  return Array.isArray(data) ? data : data.results ?? [];
}

class AssistantService {
  // --- assistentes (J07: classe GENERAL — H.Bot, H.Lex…) ---

  async listAssistants(): Promise<AssistantSummary[]> {
    const response = await apiService.get<Paginated<AssistantSummary>>('/assistant/assistants/');
    return unwrapList(response.data);
  }

  // --- conversas ---

  async listConversations(agentId?: string): Promise<AssistantConversationListItem[]> {
    const response = await apiService.get<Paginated<AssistantConversationListItem>>(
      '/assistant/conversations/',
      agentId ? { params: { agent_id: agentId } } : undefined,
    );
    return unwrapList(response.data);
  }

  async createConversation(agentId?: string, title?: string): Promise<AssistantConversationDetail> {
    const response = await apiService.post<AssistantConversationDetail>('/assistant/conversations/', {
      title: title ?? 'Nova conversa',
      ...(agentId ? { agent_id: agentId } : {}),
    });
    return response.data;
  }

  async getConversation(conversationId: string): Promise<AssistantConversationDetail> {
    const response = await apiService.get<AssistantConversationDetail>(
      `/assistant/conversations/${conversationId}/`,
    );
    return response.data;
  }

  async updateConversation(
    conversationId: string,
    payload: { title?: string; archived?: boolean },
  ): Promise<AssistantConversationDetail> {
    const response = await apiService.patch<AssistantConversationDetail>(
      `/assistant/conversations/${conversationId}/`,
      payload,
    );
    return response.data;
  }

  async archiveConversation(conversationId: string): Promise<void> {
    await apiService.delete(`/assistant/conversations/${conversationId}/`);
  }

  // --- empresa ativa / geração branded (G14) ---

  async listCompanies(): Promise<CompanyOption[]> {
    const response = await apiService.get<{ companies: CompanyOption[] }>('/assistant/companies/');
    return response.data.companies ?? [];
  }

  async setConversationCompany(
    conversationId: string,
    company: string,
    saveAsDefault = false,
  ): Promise<AssistantConversationDetail> {
    const response = await apiService.post<AssistantConversationDetail>(
      `/assistant/conversations/${conversationId}/company/`,
      { company, save_as_default: saveAsDefault },
    );
    return response.data;
  }

  async listMessages(conversationId: string): Promise<AssistantMessage[]> {
    const response = await apiService.get<Paginated<AssistantMessage>>(
      `/assistant/conversations/${conversationId}/messages/`,
    );
    return unwrapList(response.data);
  }

  // --- anexos (G04) ---

  async listAttachments(conversationId: string): Promise<AssistantAttachment[]> {
    const response = await apiService.get<Paginated<AssistantAttachment>>(
      `/assistant/conversations/${conversationId}/attachments/`,
    );
    return unwrapList(response.data);
  }

  async uploadAttachment(conversationId: string, file: File): Promise<AssistantAttachment> {
    const response = await apiService.uploadFile<AssistantAttachment>(
      `/assistant/conversations/${conversationId}/attachments/`,
      file,
      undefined,
      // 120s: o backend extrai o texto do anexo de forma síncrona (pypdf + OCR de
      // PDF escaneado), o que pode passar bem dos 30s padrão em arquivos médios.
      120_000,
    );
    return response.data;
  }

  async deleteAttachment(conversationId: string, attachmentId: string): Promise<void> {
    await apiService.delete(
      `/assistant/conversations/${conversationId}/attachments/${attachmentId}/`,
    );
  }

  // --- arquivo gerado (G03) ---

  async getFileUrl(fileId: string): Promise<string | null> {
    const response = await apiService.get<{ url: string }>(`/assistant/files/${fileId}/url/`);
    return response.data.url ?? null;
  }

  // --- memória (G07) ---

  async getMemory(): Promise<AssistantMemoryResponse> {
    const response = await apiService.get<AssistantMemoryResponse>('/assistant/memory/');
    return response.data;
  }

  async addMemory(content: string): Promise<AssistantSavedMemory> {
    const response = await apiService.post<AssistantSavedMemory>('/assistant/memory/', { content });
    return response.data;
  }

  async updateMemory(id: string, content: string): Promise<AssistantSavedMemory> {
    const response = await apiService.patch<AssistantSavedMemory>(`/assistant/memory/${id}/`, {
      content,
    });
    return response.data;
  }

  async deleteMemory(id: string): Promise<void> {
    await apiService.delete(`/assistant/memory/${id}/`);
  }
}

export default new AssistantService();
